import { Box, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import pluralize from "pluralize";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { entityKeys } from "../../../../api/queryKeys";
import { DetectionStatus, QueryParam } from "../../../../utils/constants";
import EntityDrawer from "../../../stack/environment/components/EntityDrawer/EntityDrawer";
import { useEntities } from "../../../stack/environment/hooks/useEntities";
import DetectionLabelIcon from "./DetectionLabelIcon";
import DetectionTimeline from "./DetectionTimeline";

const DetectionsTab = ({ detections, Header, handleMoveToControl = null }) => {
  const [activeDetections, setActiveDetections] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackEvent, fetchEntityById } = useAPI();
  const colors = useColors();
  const { flattenEntity } = useEntities();

  const getDetectionTitle = (activeEventsCount, totalEventsCount) => {
    return (
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          flexShrink: 0,
          color: colors.text_primary,
        }}
      >
        {activeEventsCount} {pluralize("Active event", activeEventsCount)}
        {totalEventsCount > activeEventsCount
          ? ` and ${totalEventsCount - activeEventsCount} ${pluralize(
              "resolved event",
              totalEventsCount - activeEventsCount
            )}`
          : ""}
      </Typography>
    );
  };

  useEffect(() => {
    if (detections) {
      const activeDetections = detections.filter((detection) => {
        return detection.status !== DetectionStatus.RESOLVED;
      });
      setActiveDetections(activeDetections);
    }
  }, [detections]);

  const onEntityDrawerClose = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete(QueryParam.entity);
    setSearchParams(updatedSearchParams);
    trackEvent("User closed the entity drawer");
  };

  // Get the entity from URL params for the drawer
  const viewedEntityId = decodeURIComponent(
    searchParams.get(QueryParam.entity) || ""
  );

  // Fetch entity data for the selected entity using the new single entity endpoint
  const { data: viewedEntity } = useQuery({
    queryKey: entityKeys.entity(viewedEntityId),
    queryFn: () => fetchEntityById(viewedEntityId),
    enabled: !!viewedEntityId,
    select: (response) => {
      if (!response?.data) return null;
      return flattenEntity(response.data);
    },
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            backgroundColor: colors.grey_50,
            pt: 3,
          }}
        >
          <Header />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            margin: "0px 2px",
            boxShadow: " 0px 0px 2px 0px " + colors.error_dark,
            background: colors.detections_bg,
            borderRadius: "8px",
            padding: "16px",
            gap: 1,
          }}
        >
          <DetectionLabelIcon />

          {getDetectionTitle(
            activeDetections?.length || 0,
            detections?.length || 0
          )}
        </Box>
        <DetectionTimeline
          detections={detections}
          handleMoveToControl={handleMoveToControl}
        />
      </Box>
      <EntityDrawer
        openDrawer={!!viewedEntity}
        selectedEntity={viewedEntity}
        onClose={onEntityDrawerClose}
      />
    </>
  );
};

export default DetectionsTab;
